import type { Release } from '../types'
import { Box, CloudUpload } from './Icons'

type Props = {
  releases: Release[]
  archivingId: string | null
  canManage?: boolean
  onPublish: () => void
  onArchive: (releaseId: string) => Promise<void>
}

function dateLabel(value: string | null) {
  if (!value) return 'Draft'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date(value))
}

export function Releases({
  releases,
  archivingId,
  canManage = true,
  onPublish,
  onArchive,
}: Props) {
  return (
    <section className="releases-section">
      <header>
        <h2>Latest releases</h2>
        <button className="primary-button" type="button" disabled={!canManage} onClick={onPublish}>
          <CloudUpload size={16} />
          Publish release
        </button>
      </header>
      <div className="table-scroll">
        <table className="release-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Version</th>
              <th>Channel</th>
              <th>Artifacts</th>
              <th>Signing</th>
              <th>Downloads</th>
              <th>Published</th>
              <th>Published by</th>
              <th aria-label="Release actions" />
            </tr>
          </thead>
          <tbody>
            {releases.map((release) => (
              <tr key={release.id}>
                <td>
                  <span className="product-cell">
                    <Box size={16} />
                    {release.product}
                  </span>
                </td>
                <td className="mono">{release.version}</td>
                <td className="capitalize">{release.channel}</td>
                <td>
                  {release.artifacts_ready} / {release.artifacts_total}
                </td>
                <td>
                  <span className="status-text">
                    <i
                      className={`square ${
                        release.archived
                          ? 'muted'
                          : release.signing_status === 'signed'
                            ? 'success'
                            : 'muted'
                      }`}
                    />
                    <span className="capitalize">
                      {release.archived ? 'archived' : release.signing_status}
                    </span>
                  </span>
                </td>
                <td>
                  <span className="download-metric">
                    <strong>{release.download_count.toLocaleString()}</strong>
                    <small>{release.unique_devices.toLocaleString()} devices</small>
                  </span>
                </td>
                <td>
                  {release.archived
                    ? `Archived ${dateLabel(release.archived_at)}`
                    : dateLabel(release.published_at)}
                </td>
                <td>{release.published_by ?? '—'}</td>
                <td>
                  <button
                    className="archive-release"
                    type="button"
                    disabled={!canManage || release.archived || archivingId !== null}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Archive ${release.product} ${release.version}? Downloads will stop and private artifacts will be deleted.`,
                        )
                      ) {
                        void onArchive(release.id)
                      }
                    }}
                  >
                    {archivingId === release.id ? 'Archiving…' : 'Archive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
